<script lang="ts">
import Section from '@/framework/base/classes/Section';
import ImportDialog from '@/framework/admin/components/ImportDialog.vue';
import { AdminConfig } from '@/generated/classes/Admin';
import AdminAttrib from '@/generated/classes/AdminAttrib';
import SectionTestGraph from '@/framework/graph/components/Graph.vue';
import User from '@/generated/classes/User';
import SectionUser from '@/custom/sections/SectionUser.vue';
import Etablissement from '@/generated/classes/Etablissement';
import SectionEtablissementIdentiteExtend from '@/extend/sections/SectionEtablissementIdentiteExtend.vue';
import SectionEditArticles from '@/custom/sections/SectionEditArticles.vue';
import SectionContratTemplateConfigExtend from '@/extend/sections/SectionContratTemplateConfigExtend.vue';
import ContratTemplate from '@/generated/classes/ContratTemplate';
import { storeActualite, storeContratTemplate, storeUser } from '@/generated/stores';
import SectionAdminWizard from '@/framework/wizard/components/SectionAdminWizard.vue';
import SectionAdminActualite from '@/custom/sections/SectionAdminActualite.vue';
import { ListChoix } from '@/extend/classes/ListData';
import { NotifDefTypes } from '@/generated/classes/NotifDef';
import { defineComponent } from "vue";
import {type EditRessource} from "@/framework/base/helpers/Ressources.ts";

export function getAdminAttribs(table: string, attribs: AdminAttrib[]) {
  if (table == 'ContratTemplates') {
    const clientId = attribs.find(a => a.value == 'clientId');
    if (clientId) {
      clientId.foreignClass = 'CLIENT';
      clientId.foreignTableName = 'Clients';
    }
  }
  return attribs;
}

function contratTemplateConfigSection(contratTemplate: ContratTemplate): Section {
  const section = new Section();
  section.component = SectionContratTemplateConfigExtend;
  section.type = 'contratTemplate';
  section.data = contratTemplate;
  section.showButton = true;
  return section;
}

export function cloneItem(table: string, data: unknown): Section | undefined {
  if (table != 'ContratTemplates') {
    return undefined;
  }

  const sourceTemplate = data as ContratTemplate;
  if (!sourceTemplate || sourceTemplate.id <= 0) {
    return undefined;
  }

  const contratTemplate = storeContratTemplate.createNewContratTemplate();
  contratTemplate.sourceTemplateId = sourceTemplate.id;
  contratTemplate.name = sourceTemplate.name;
  contratTemplate.titre = sourceTemplate.titre;
  contratTemplate.useConvention = sourceTemplate.useConvention;
  contratTemplate.active = false;
  return contratTemplateConfigSection(contratTemplate);
}

export function editItem(table: string, config: AdminConfig, data: unknown): Section | undefined {
  if (table == 'Graphs') {
    const section = new Section();
    section.component = SectionTestGraph;
    section.type = 'graph';
    section.data = data;
    section.showButton = true;
    return section;
  } else if (table == 'Users') {
    const section = new Section();
    section.component = SectionUser;
    section.type = 'user';
    section.data = User.fromJson(data as User);
    section.showButton = true;
    return section;
  } else if (table == 'Etablissements') {
    const etablissement = Etablissement.fromJson(data as Etablissement);
    if (etablissement.notifDefType == NotifDefTypes.ACTION) {
      const section = new Section();
      section.component = SectionEtablissementIdentiteExtend;
      section.type = 'etablissement';
      section.data = etablissement;
      section.showButton = true;
      return section;
    } else {
      return undefined;
    }
  } else if (table == 'Wizards') {
    const section = new Section();
    section.component = SectionAdminWizard;
    section.type = 'data';
    section.data = data;
    return section;
  } else if (table == 'Actualites') {
    const section = new Section();
    section.component = SectionAdminActualite;
    section.type = 'actualite';
    section.data = data;
    return section;
  } else if (table == 'ContratTemplates' && (data as ContratTemplate).id > 0) {
    const section = new Section();
    section.component = SectionEditArticles;
    section.type = 'contratTemplateId';
    section.data = ContratTemplate.fromJson(data as ContratTemplate).id;
    section.fullscreen = true;
    section.showButton = true;
    return section;
  } else {
    return undefined;
  }
}

export function addItem(table: string, config: AdminConfig, data: unknown): Section | undefined {
  if (table == 'Users') {
    const section = new Section();
    section.component = SectionUser;
    section.type = 'user';
    const user = storeUser.createNewUser();
    user.canUpdate = true;
    section.data = user;
    section.showButton = true;
    return section;
  } else if (table == 'Actualites') {
    const section = new Section();
    const actualite = storeActualite.createNewActualite();
    section.component = SectionAdminActualite;
    section.type = 'actualite';
    section.data = actualite;
    return section;
  } else if (table == 'ContratTemplates') {
    const contratTemplate = storeContratTemplate.createNewContratTemplate();
    contratTemplate.active = false;
    return contratTemplateConfigSection(contratTemplate);
  } else {
    return undefined;
  }
}

export default defineComponent({
  components: {
    ImportDialog,
    SectionTestGraph,
    SectionUser,
    SectionEtablissementIdentiteExtend,
    SectionEditArticles,
    SectionContratTemplateConfigExtend,
    SectionAdminWizard
  },
  data() {
    const groupeRetenuesParentChoix: ListChoix[] = [];
    const groupeRetenuesChoix: ListChoix[] = [];

    return {
      groupeRetenuesChoix,
      groupeRetenuesParentChoix
    };
  },
  methods: {
    getData(table: string, attribs: AdminAttrib[], data: any[]): unknown[] {
      // if (table == 'Retenues') {
      //   data.forEach((rec) => {
      //     const choix = FicheAdminExtend.groupeRetenuesChoix.find(a => a.value == rec.groupeId);
      //     if (choix) {
      //       rec.groupeId = choix.value;
      //     } else if (rec.groupeId == 0) {
      //       rec.groupeId = '';
      //     }
      //   });
      // } else if (table == 'GroupeRetenues') {
      //   data.forEach((rec) => {
      //     const choix = FicheAdminExtend.groupeRetenuesParentChoix.find(a => a.value == rec.parentId);
      //     if (choix) {
      //       rec.parentId = choix.value;
      //     } else if (rec.parentId == 0) {
      //       rec.parentId = '';
      //     }
      //   });
      // }
      return data;
    },
    getFilterItems(adminAttrib: AdminAttrib, key: number | string, d: any) {
      return adminAttrib.choix.find(c => c.value == d[key])?.text;
    }
  }
})

</script>

<style scoped></style>
